<script setup lang="ts">
import { computed } from 'vue';
import { date, useQuasar } from 'quasar';
import type { User } from 'src/modules/User/models/User';
import type { License } from 'src/modules/Child/models/Child';
import BCard from 'src/shared/components/base/BCard/BCard.vue';

interface Props {
  currentUser: User | null;
}

const props = defineProps<Props>();
const $q = useQuasar();

const license = computed<License | null>(() => {
  const childLicense = props.currentUser?.child?.license;
  // Handle if license is an array or single object
  if (Array.isArray(childLicense)) {
    return childLicense[0] || null;
  }
  return childLicense || null;
});

const hasLicense = computed(() => {
  return !!(license.value?.assigned_at && license.value?.valid_until);
});

const isValidLicense = computed(() => {
  if (!license.value?.valid_until) return false;
  return new Date(license.value.valid_until) >= new Date();
});

const formattedStartDate = computed(() => {
  if (!license.value?.assigned_at) return '-';
  return date.formatDate(license.value.assigned_at, 'DD/MM/YYYY');
});

const formattedValidUntil = computed(() => {
  if (!license.value?.valid_until) return '-';
  return date.formatDate(license.value.valid_until, 'DD/MM/YYYY');
});
</script>

<template>
  <BCard
    size="sm"
    class="rounded-borders-xl"
    :class="$q.dark.isActive ? 'shadow-card-dark' : 'shadow-card bg-white'"
    :dark="$q.dark.isActive"
  >
    <q-card-section>
      <q-item>
        <q-item-section>
          <q-item-label class="text-h6">{{ $t('LICENSE') }}</q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-item-label>
            <q-badge
              :color="isValidLicense ? 'positive' : 'negative'"
              :label="isValidLicense ? $t('ACTIVE') : $t('LICENSE_EXPIRED')"
            />
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-card-section>

    <q-separator />

    <q-card-section v-if="!hasLicense">
      <div class="text-center q-py-md">
        <q-icon
          name="info"
          size="48px"
          color="grey-5"
        />
        <div class="text-body2 text-muted q-mt-md">
          {{ $t('NO_LICENSE') }}
        </div>
      </div>
    </q-card-section>

    <q-card-section v-else>
      <q-list dense>
        <q-item>
          <q-item-section>
            <q-item-label caption>{{ $t('START_DATE') }}</q-item-label>
            <q-item-label>{{ formattedStartDate }}</q-item-label>
          </q-item-section>
        </q-item>

        <q-separator
          spaced
          inset
        />

        <q-item>
          <q-item-section>
            <q-item-label caption>{{ $t('VALID_UNTIL') }}</q-item-label>
            <q-item-label
              :class="isValidLicense ? 'text-positive' : 'text-negative'"
            >
              {{ formattedValidUntil }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-card-section>
  </BCard>
</template>
